import { useDark, useNetwork } from "@vueuse/core";
import type { IRowNode } from "ag-grid-community";
import equal from "deep-equal";
import type { SerializedEditorState } from "lexical";
import moment from "moment";
import { defineStore } from "pinia";
import { markRaw, nextTick, watch } from "vue";

import type { WebsocketManager } from "~/actions/websockets";
import { CURRENT_USER_PSEUDO_USER_KEY } from "~/common/filter";
import {
  getDefaultBoardGroupBy,
  getDefaultListGroupBy,
  getGroupByDefinitionList,
  UNGROUPED_PSEUDO_GROUP_BY,
  UNSET_PSEUDO_SUBGROUP_BY,
} from "~/common/groupBy";
import { getShownPropertyWithConfigList } from "~/common/properties";
import type ChatPanel from "~/components/chat/ChatPanel.vue";
import type CmdCtr from "~/components/cmdCtr/CmdCtr.vue";
import type FilterArea from "~/components/filters/FilterArea.vue";
import type TaskDetail from "~/components/task/TaskDetail.vue";
import type Tcm from "~/components/task/Tcm.vue";
import type TaskTopbarInfo from "~/components/topbar/TaskTopbarInfo.vue";
import type Topbar from "~/components/topbar/Topbar.vue";
import type Board from "~/components/visualization/board/Board.vue";
import type Calendar from "~/components/visualization/calendar/Calendar.vue";
import {
  COMPLETED_STATUS_KINDS,
  DEFAULT_ASSIGNEES_FILTER_PARTIAL,
  DEFAULT_DARTBOARD_FILTER_PARTIAL,
  DEFAULT_STATUS_FILTER_PARTIAL,
  NONACTIVE_DARTBOARD_KINDS,
  TRASH_FILTER,
} from "~/components/visualization/constants";
import type List from "~/components/visualization/list/List.vue";
import type Roadmap from "~/components/visualization/roadmap/Roadmap.vue";
import type SubtasksList from "~/components/visualization/subtasks/SubtasksList.vue";
import {
  getBoardCalendarOrWorkflowVisualization,
  getDefaultVisualization,
  getInboxVisualization,
  getListVisualization,
  type Visualization,
} from "~/components/visualization/visualization";
import type Workflow from "~/components/visualization/workflow/Workflow.vue";
import { getCommandsList } from "~/constants/command";
import { COLOR_BY_LAYOUT_KINDS, GROUP_BY_LAYOUT_KINDS } from "~/constants/layout";
import {
  AGENTS_PSEUDO_PAGE,
  DASHBOARDS_PSEUDO_PAGE,
  HOME_PSEUDO_PAGE,
  INBOX_PSEUDO_PAGE,
  NOT_FOUND_PSEUDO_PAGE,
  VIEWS_PSEUDO_PAGE,
} from "~/constants/page";
import {
  AI_PROPERTY_PSEUDO_DUID,
  ASSIGNEES_COLUMN_MOBILE_WIDTH,
  CONFIG_PROPERTY_PSEUDO_DUID,
  DARTBOARD_KIND_PROPERTY_PSEUDO_DUID,
  DEFAULT_COLUMN_WIDTH_LIMITS,
  PROPERTY_KIND_TO_DEFAULT_WIDTH_MAP,
  STATUS_COLUMN_MOBILE_WIDTH,
  STATUS_KIND_PROPERTY_PSEUDO_DUID,
  TITLE_COLUMN_MAX_MIN_WIDTH,
  TITLE_COLUMN_MIN_WIDTH,
} from "~/constants/property";
import { UNCOLORED_PSEUDO_COLOR_BY } from "~/constants/pseudoDuids";
import {
  CommandId,
  Entitlement,
  EventKind,
  FilterApplicability,
  FilterConnector,
  LayoutKind,
  ModalKind,
  PageKind,
  Placement,
  PropertyKind,
  Subscription,
  SubtaskDisplayMode,
  SummaryStatisticKind,
  TaskDetailMode,
  TutorialName,
  ViewKind,
  WebhookEventKind,
} from "~/shared/enums";
import type {
  AttachmentCreate,
  CalendarDisplayPeriod,
  Chat,
  CommandSection,
  ContextMenu,
  Dartboard,
  DartboardUpdate,
  Doc,
  DropdownMenuSection,
  EventKindFull,
  FeedbackTooltip,
  Filter,
  Folder,
  Form,
  GroupByDefinition,
  Layout,
  LayoutConfig,
  LayoutConfigBoard,
  LayoutConfigCalendar,
  LayoutConfigList,
  LayoutConfigRoadmap,
  Page,
  PageIconOption,
  PageWithPermissions,
  Property,
  PropertyConfig,
  RemapModalConfig,
  Sort,
  Task,
  TaskLinkInModal,
  TaskPreviewTooltip,
  ViewUpdate,
  VisualizationItem,
} from "~/shared/types";
import {
  deepCopy,
  getDartboardLink,
  getDashboardLink,
  getDocLink,
  getFolderLink,
  getPageDisplayName,
  getPageLink,
  getPublicFormLink,
  getTaskLink,
  getViewLink,
  spacePermissionsSame,
} from "~/utils/common";
import { makeStringComparator } from "~/utils/comparator";
import { allThrottleManagers } from "~/utils/throttleManager";
import type DashboardsRoot from "~/views/DashboardsRootView.vue";
import type DocView from "~/views/DocView.vue";
import type FolderRootView from "~/views/FolderRootView.vue";
import type InboxView from "~/views/InboxView.vue";
import type ViewsRoot from "~/views/ViewsRootView.vue";

const TASK_DETAIL_MODE_TO_EVENT_KIND_MAP: Record<TaskDetailMode, EventKind> = {
  [TaskDetailMode.RIGHTBAR]: EventKind.USAGE_OPEN_RIGHTBAR,
  [TaskDetailMode.FULLSCREEN]: EventKind.USAGE_OPEN_FULLSCREEN,
  [TaskDetailMode.OVERLAY]: EventKind.USAGE_OPEN_TASK_OVERLAY,
};

const LEFTBAR_HOVER_CLOSE_TIMEOUT_MS = 200;

type State = {
  // global app data
  eventKinds: EventKindFull[];
  pageIconOptions: PageIconOption[];
  webhookEventKinds: WebhookEventKind[];
  pendingAttachmentCreates: AttachmentCreate[];
  // components
  topbar: InstanceType<typeof Topbar> | null;
  list: InstanceType<typeof List> | null;
  board: InstanceType<typeof Board> | null;
  calendar: InstanceType<typeof Calendar> | null;
  roadmap: InstanceType<typeof Roadmap> | null;
  workflow: InstanceType<typeof Workflow> | null;
  spaceOrFolder: InstanceType<typeof FolderRootView> | null;
  inbox: InstanceType<typeof InboxView> | null;
  tcmSubtasksList: InstanceType<typeof SubtasksList> | null;
  taskDetailSubtasksList: InstanceType<typeof SubtasksList> | null;
  taskDetail: InstanceType<typeof TaskDetail> | null;
  taskTopbarInfo: InstanceType<typeof TaskTopbarInfo> | null;
  filterArea: InstanceType<typeof FilterArea> | null;
  searchFilterArea: InstanceType<typeof FilterArea> | null;
  tcm: InstanceType<typeof Tcm> | null;
  chatPanel: InstanceType<typeof ChatPanel> | null;
  agentChatPanel: InstanceType<typeof ChatPanel> | null;
  taskChatPanel: InstanceType<typeof ChatPanel> | null;
  cmdCtr: InstanceType<typeof CmdCtr> | null;
  doc: InstanceType<typeof DocView> | null;
  viewsRoot: InstanceType<typeof ViewsRoot> | null;
  dashboardsRoot: InstanceType<typeof DashboardsRoot> | null;
  // managers
  // TODO managers here could probably be handled better
  websocketManager?: WebsocketManager;
  taskDropTargets: Map<string, HTMLElement>;
  speechRecordingDuids: Set<string>;
  hasInvalidAgentState: boolean;
  cachedNewTaskInput: SerializedEditorState | null;
  newTask: { focus: () => void } | null;
  dragging: { group: string; category: string } | null;
  // instance-specific
  currentPageConfig?: { kind: PageKind; duid: string };
  layoutDuid?: string;
  selectedTaskDuids: Set<string>;
  selectedChatDuid: string | null;
  loadingTaskProperties: Set<string>;
  search: string | null;
  // TODO improve management of hover state
  hoverClosedWithBarOpen: boolean;
  hoverRow: IRowNode<VisualizationItem> | null;
  hoverGroupId: string | null;
  lastQueryParamMap: Map<string, string>;
  // modals
  contextMenu: ContextMenu | null;
  taskPreviewTooltip: TaskPreviewTooltip | null;
  feedbackTooltip: FeedbackTooltip | null;
  settingsModalOpen: boolean;
  tcmOpen: boolean;
  startBuildModeOverlayOpen: boolean;
  formInInternalModalDuid: string | null;
  upgradeModalConfig: {
    featureAction: string;
    subscription: Subscription;
  } | null;
  deleteModalConfig: {
    title: string;
    description: string;
    confirmText?: string;
    cancelText?: string;
    onDelete: () => void;
  } | null;
  movePageModalConfig: { page: Dartboard | Folder; newSpaceDuid: string } | null;
  appDownloadModalOpen: boolean;
  commandCenterOpen: boolean;
  feedbackModalOpen: boolean;
  shortcutsModalOpen: boolean;
  searchModalOpen: boolean;
  searchModalVisible: boolean;
  switchToDesktopModalOpen: boolean;
  permissionsModalConfig: { kind: PageKind | null; duid: string } | null;
  linkOpenInModalConfig: { taskDuid: string; linkDuid?: string } | null;
  mediaOpenInFullscreen: {
    attachmentFileUrl: string;
    attachmentName: string;
    kind: string;
    entityName: string;
  } | null;
  onboardingModalOpen: boolean;
  onboardingVideoModalOpen: boolean;
  remapModal: RemapModalConfig | null;
  agentCreationModal: { pendingSetupDuid: string | null } | null;
  // appearance
  taskDetailOpen: boolean;
  taskDetailLoading: boolean;
  taskDetailMode: TaskDetailMode | null;
  taskDetailPaneWidthPx: number | null;
  chatFullscreen: boolean;
  leftbarInCollapsibleMode: boolean;
  leftbarHoverOpen: boolean;
  leftbarHoverCloseTimeout: ReturnType<typeof setTimeout> | null;
  leftbarLockSources: Set<string>;
  mobileLeftbarOpen: boolean;
  docOpenInFullscreenDuid: string | null;
  pendingAgentImageGenerationDuid: string | null;
  trialStartedSuppressed: boolean;
  isModalOpen: boolean;
  lastSettingsPage: Record<string, string>;
  lastProjectsModePath: string | null;
  showStandupTimer: boolean;
};

const makeLoadingAiTaskPropertyKey = (taskDuid: string, propertyDuid: string) =>
  JSON.stringify([taskDuid, propertyDuid]);

const useAppStore = defineStore("AppStore", {
  state: (): State =>
    ({
      // global app data
      eventKinds: [],
      pageIconOptions: [],
      webhookEventKinds: [],
      pendingAttachmentCreates: [],
      // components
      topbar: null,
      list: null,
      board: null,
      calendar: null,
      roadmap: null,
      workflow: null,
      spaceOrFolder: null,
      inbox: null,
      tcmSubtasksList: null,
      taskDetailSubtasksList: null,
      taskDetail: null,
      taskTopbarInfo: null,
      filterArea: null,
      searchFilterArea: null,
      tcm: null,
      chatPanel: null,
      agentChatPanel: null,
      taskChatPanel: null,
      cmdCtr: null,
      doc: null,
      viewsRoot: null,
      dashboardsRoot: null,
      // managers
      websocketManager: undefined,
      taskDropTargets: new Map(),
      speechRecordingDuids: new Set(),
      hasInvalidAgentState: false,
      cachedNewTaskInput: null,
      newTask: null,
      dragging: null,
      // instance-specific
      currentPageConfig: undefined,
      layoutDuid: undefined,
      selectedTaskDuids: new Set(),
      selectedChatDuid: null,
      loadingTaskProperties: new Set(),
      search: null,
      hoverClosedWithBarOpen: false,
      hoverRow: null,
      hoverGroupId: null,
      lastQueryParamMap: new Map(),
      // modals
      contextMenu: null,
      taskPreviewTooltip: null,
      feedbackTooltip: null,
      settingsModalOpen: false,
      tcmOpen: false,
      startBuildModeOverlayOpen: false,
      formInInternalModalDuid: null,
      upgradeModalConfig: null,
      deleteModalConfig: null,
      movePageModalConfig: null,
      appDownloadModalOpen: false,
      feedbackModalOpen: false,
      shortcutsModalOpen: false,
      searchModalOpen: false,
      searchModalVisible: false,
      switchToDesktopModalOpen: false,
      commandCenterOpen: false,
      permissionsModalConfig: null,
      linkOpenInModalConfig: null,
      mediaOpenInFullscreen: null,
      onboardingModalOpen: false,
      onboardingVideoModalOpen: false,
      remapModal: null,
      agentCreationModal: null,
      // appearance
      leftbarInCollapsibleMode: false,
      leftbarHoverOpen: false,
      leftbarHoverCloseTimeout: null,
      leftbarLockSources: new Set(),
      mobileLeftbarOpen: false,
      taskDetailOpen: false,
      taskDetailLoading: false,
      taskDetailMode: null,
      taskDetailPaneWidthPx: null,
      chatFullscreen: false,
      docOpenInFullscreenDuid: null,
      pendingAgentImageGenerationDuid: null,
      trialStartedSuppressed: false,
      isModalOpen: false,
      lastSettingsPage: { settings: "properties" },
      lastProjectsModePath: null,
      showStandupTimer: false,
    }) satisfies State,
  getters: {
    // instance-specific
    currentPage(): Page | undefined {
      const dataStore = this.$useDataStore();

      if (!this.currentPageConfig) {
        return undefined;
      }

      switch (this.currentPageConfig.kind) {
        case PageKind.AGENTS: {
          return AGENTS_PSEUDO_PAGE;
        }
        case PageKind.DARTBOARD: {
          return dataStore.getDartboardByDuid(this.currentPageConfig.duid);
        }
        case PageKind.DASHBOARD: {
          return dataStore.getDashboardByDuid(this.currentPageConfig.duid);
        }
        case PageKind.DASHBOARDS_ROOT: {
          return DASHBOARDS_PSEUDO_PAGE;
        }
        case PageKind.DOC: {
          return dataStore.getDocByDuidIfLoaded(this.currentPageConfig.duid);
        }
        case PageKind.FOLDER: {
          return dataStore.getFolderByDuid(this.currentPageConfig.duid);
        }
        case PageKind.HOME: {
          return HOME_PSEUDO_PAGE;
        }
        case PageKind.INBOX: {
          return INBOX_PSEUDO_PAGE;
        }
        case PageKind.NOT_FOUND: {
          return NOT_FOUND_PSEUDO_PAGE;
        }
        case PageKind.SPACE: {
          return dataStore.getSpaceByDuid(this.currentPageConfig.duid);
        }
        case PageKind.VIEW: {
          return dataStore.getViewByDuid(this.currentPageConfig.duid);
        }
        case PageKind.VIEWS_ROOT: {
          return VIEWS_PSEUDO_PAGE;
        }
        default: {
          throw new Error(`Unknown page kind: ${this.currentPageConfig.kind}`);
        }
      }
    },
    hasActiveSpeechRecording(): boolean {
      return this.speechRecordingDuids.size > 0;
    },
    leftbarLocked(): boolean {
      return this.leftbarLockSources.size > 0;
    },
    currentDartboardDuid(): string | null {
      return this.currentPage?.pageKind === PageKind.DARTBOARD ? this.currentPage.duid : null;
    },
    currentDartboardFilters(): Filter[] {
      return [
        ...(this.currentDartboardDuid &&
        this.currentPage?.kind !== ViewKind.TRASH &&
        this.currentPage?.kind !== ViewKind.SEARCH
          ? [
              {
                id: "dartboard-filter",
                propertyDuid: this.$useDataStore().defaultDartboardProperty.duid,
                applicability: FilterApplicability.IS,
                connector: FilterConnector.AND,
                values: [this.currentDartboardDuid],
              },
            ]
          : []),
        ...(this.$usePageStore().isPublicView ? [] : this.filters),
      ];
    },
    currentDartboardOrDefault(): Dartboard | undefined {
      return this.currentPage?.pageKind === PageKind.DARTBOARD
        ? this.currentPage
        : this.$useDataStore().defaultDartboard;
    },
    effectiveTaskDetailMode(): TaskDetailMode | null {
      const currentMode = this.taskDetailMode;
      if (!this.taskDetailOpen || currentMode === null) {
        return null;
      }

      return this.$usePageStore().isMainContentTooNarrowForListAndTaskDetail ? TaskDetailMode.FULLSCREEN : currentMode;
    },
    alwaysShownPropertyDuids(): string[] {
      if (
        !this.currentPage ||
        !(this.currentPage.pageKind === PageKind.DARTBOARD || this.currentPage.pageKind === PageKind.VIEW)
      ) {
        if (this.taskDetailOpen && this.selectedTaskDuids.size === 1) {
          const dataStore = this.$useDataStore();
          const taskDuid = [...this.selectedTaskDuids][0];
          const task = dataStore.getTaskByDuidIfLoaded(taskDuid);
          if (task) {
            const dartboard = dataStore.getDartboardByDuid(task.dartboardDuid);
            if (dartboard) {
              return dartboard.alwaysShownPropertyDuids;
            }
          }
        }
        return [];
      }
      return this.currentPage.alwaysShownPropertyDuids;
    },
    alwaysHiddenPropertyDuids(): string[] {
      if (
        !this.currentPage ||
        !(this.currentPage.pageKind === PageKind.DARTBOARD || this.currentPage.pageKind === PageKind.VIEW)
      ) {
        if (this.taskDetailOpen && this.selectedTaskDuids.size === 1) {
          const dataStore = this.$useDataStore();
          const taskDuid = [...this.selectedTaskDuids][0];
          const task = dataStore.getTaskByDuidIfLoaded(taskDuid);
          if (task) {
            const dartboard = dataStore.getDartboardByDuid(task.dartboardDuid);
            if (dartboard) {
              return dartboard.alwaysHiddenPropertyDuids;
            }
          }
        }
        return [];
      }
      return this.currentPage.alwaysHiddenPropertyDuids;
    },
    propertyOrderDuids(): string[] {
      if (
        !this.currentPage ||
        !(this.currentPage.pageKind === PageKind.DARTBOARD || this.currentPage.pageKind === PageKind.VIEW)
      ) {
        if (this.taskDetailOpen && this.selectedTaskDuids.size === 1) {
          const dataStore = this.$useDataStore();
          const taskDuid = [...this.selectedTaskDuids][0];
          const task = dataStore.getTaskByDuidIfLoaded(taskDuid);
          if (task) {
            const dartboard = dataStore.getDartboardByDuid(task.dartboardDuid);
            if (dartboard) {
              return dartboard.propertyOrderDuids;
            }
          }
        }
        return [];
      }
      return this.currentPage.propertyOrderDuids;
    },
    isPropertyShown(): (property: Property) => boolean {
      return (property) => {
        // TODO might want to show hidden properties on views as well
        if (this.currentPage?.pageKind === PageKind.DASHBOARD) {
          return true;
        }
        return property.hidden
          ? this.alwaysShownPropertyDuids.includes(property.duid)
          : !this.alwaysHiddenPropertyDuids.includes(property.duid);
      };
    },
    isPropertyShownInLayout(): (property: Property, propertyConfig: PropertyConfig) => boolean {
      return (property, config) => {
        if (property.kind === PropertyKind.DEFAULT_TITLE) {
          return false;
        }
        if (this.currentPage?.kind === ViewKind.TRASH) {
          return !property.hidden;
        }
        if (this.alwaysHiddenPropertyDuids.includes(property.duid)) {
          return false;
        }
        if (
          this.alwaysShownPropertyDuids.includes(property.duid) ||
          config.alwaysShowForLayouts?.includes(this.layoutKind)
        ) {
          return true;
        }
        if (config.defaultHideForLayouts?.includes(this.layoutKind)) {
          return false;
        }
        return !property.hidden;
      };
    },
    layout(): Layout | undefined {
      if (!this.layoutDuid) {
        return undefined;
      }
      return this.$useDataStore().getLayoutByDuid(this.layoutDuid);
    },
    layoutKind(): LayoutKind {
      return this.layout?.kind ?? LayoutKind.LIST;
    },
    layoutConfig(): LayoutConfig {
      const res = this.layout?.kindConfigMap?.[this.layoutKind];
      if (res) {
        return res;
      }

      switch (this.layoutKind) {
        case LayoutKind.LIST: {
          return {
            subtaskDisplayMode: SubtaskDisplayMode.INDENTED,
            showAbsentees: true,
            groupBy: getDefaultListGroupBy(),
            collapsedGroups: [],
            hideEmptyGroups: false,
          };
        }
        case LayoutKind.BOARD: {
          return {
            subtaskDisplayMode: SubtaskDisplayMode.INDENTED,
            showAbsentees: true,
            groupBy: getDefaultBoardGroupBy(),
            subgroupBy: UNSET_PSEUDO_SUBGROUP_BY,
            collapsedGroups: [],
            collapsedSubgroups: [],
            hideEmptyGroups: false,
            displayCoverImages: false,
          };
        }
        case LayoutKind.CALENDAR: {
          return {
            subtaskDisplayMode: SubtaskDisplayMode.INDENTED,
            showAbsentees: true,
            displayPeriod: "month",
            displayPeriodCount: 1,
            colorBy: UNCOLORED_PSEUDO_COLOR_BY,
          };
        }
        case LayoutKind.ROADMAP: {
          return {
            subtaskDisplayMode: SubtaskDisplayMode.INDENTED,
            showAbsentees: true,
            pxPerDay: 10,
            taskListWidthPx: 550,
            colorBy: UNCOLORED_PSEUDO_COLOR_BY,
          };
        }
        case LayoutKind.WORKFLOW: {
          return {
            subtaskDisplayMode: SubtaskDisplayMode.INDENTED,
            showAbsentees: true,
          };
        }
        default: {
          throw new Error(`Unknown layout kind: ${this.layoutKind}`);
        }
      }
    },
    showAbsentees(): boolean {
      return (
        this.currentPage?.kind !== ViewKind.TRASH &&
        this.subtaskDisplayMode !== SubtaskDisplayMode.FLAT &&
        (this.layoutConfig.showAbsentees ?? true)
      );
    },
    groupByDefinitionList(): GroupByDefinition[] {
      const propertiesWithConfig = getShownPropertyWithConfigList([PropertyKind.DEFAULT_DARTBOARD]);

      return getGroupByDefinitionList(propertiesWithConfig);
    },
    groupBy(): string {
      if (!GROUP_BY_LAYOUT_KINDS.has(this.layoutKind)) {
        return UNGROUPED_PSEUDO_GROUP_BY;
      }

      const defaultGroupBy = (this.layoutKind === LayoutKind.LIST ? getDefaultListGroupBy : getDefaultBoardGroupBy)();
      const groupBy = (this.layoutConfig as LayoutConfigList | LayoutConfigBoard).groupBy ?? defaultGroupBy;

      // Check if the groupBy is still valid
      if (this.groupByDefinitionList.find((e) => e.property.duid === groupBy)) {
        return groupBy;
      }

      if (this.groupByDefinitionList.find((e) => e.property.duid === defaultGroupBy)) {
        return defaultGroupBy;
      }

      const nonUngroupedOptions = this.groupByDefinitionList.filter(
        (e) => e.property.duid !== UNGROUPED_PSEUDO_GROUP_BY
      );

      if (this.layoutKind === LayoutKind.BOARD && nonUngroupedOptions.length > 0) {
        return nonUngroupedOptions[0].property.duid;
      }

      return UNGROUPED_PSEUDO_GROUP_BY;
    },
    groupByDefinition(): GroupByDefinition {
      const { groupBy } = this;
      const res = this.groupByDefinitionList.find((e) => e.property.duid === groupBy);
      if (!res) {
        throw new Error(`Unknown groupBy: ${groupBy}`);
      }
      return res;
    },
    hideEmptyGroups(): boolean {
      if (!GROUP_BY_LAYOUT_KINDS.has(this.layoutKind)) {
        return false;
      }

      const layoutConfig = this.layoutConfig as LayoutConfigList | LayoutConfigBoard;
      return layoutConfig.hideEmptyGroups ?? false;
    },
    colorBy(): string {
      if (!COLOR_BY_LAYOUT_KINDS.has(this.layoutKind)) {
        return UNCOLORED_PSEUDO_COLOR_BY;
      }

      const defaultColorBy = UNCOLORED_PSEUDO_COLOR_BY;
      const layoutConfig = this.layoutConfig as LayoutConfigRoadmap | LayoutConfigCalendar;
      const colorBy = layoutConfig.colorBy ?? defaultColorBy;

      // Check if the colorBy is still valid
      if (this.groupByDefinitionList.find((e) => e.property.duid === colorBy)) {
        return colorBy;
      }

      return defaultColorBy;
    },
    colorByDefinition(): GroupByDefinition {
      const { colorBy } = this;
      const res = this.groupByDefinitionList.find((e) => e.property.duid === colorBy);
      if (!res) {
        throw new Error(`Unknown colorBy: ${colorBy}`);
      }
      return res;
    },
    subgroupBy(): string {
      if (this.layoutKind !== LayoutKind.BOARD) {
        return UNSET_PSEUDO_SUBGROUP_BY;
      }
      const layoutConfig = this.layoutConfig as LayoutConfigBoard;
      return layoutConfig.subgroupBy ?? UNSET_PSEUDO_SUBGROUP_BY;
    },
    subgroupByDefinition(): GroupByDefinition | null {
      const { subgroupBy } = this;
      if (subgroupBy === UNSET_PSEUDO_SUBGROUP_BY || subgroupBy === this.groupBy) {
        return null;
      }
      return this.groupByDefinitionList.find((e) => e.property.duid === subgroupBy) ?? null;
    },
    displayCoverImages(): boolean {
      if (this.layoutKind !== LayoutKind.BOARD) {
        return false;
      }

      const layoutConfig = this.layoutConfig as LayoutConfigBoard;
      return layoutConfig.displayCoverImages ?? false;
    },
    defaultTrashFilters(): Filter[] {
      return [
        {
          ...TRASH_FILTER,
          locked: true,
        },
      ];
    },
    defaultSearchFilters(): Filter[] {
      return [
        {
          ...DEFAULT_STATUS_FILTER_PARTIAL,
          propertyDuid: STATUS_KIND_PROPERTY_PSEUDO_DUID,
          values: [...COMPLETED_STATUS_KINDS],
        },
      ];
    },
    defaultMyTasksFilters(): Filter[] {
      const dataStore = this.$useDataStore();

      return [
        {
          ...DEFAULT_DARTBOARD_FILTER_PARTIAL,
          propertyDuid: DARTBOARD_KIND_PROPERTY_PSEUDO_DUID,
          values: [...NONACTIVE_DARTBOARD_KINDS],
        },
        {
          ...DEFAULT_STATUS_FILTER_PARTIAL,
          propertyDuid: STATUS_KIND_PROPERTY_PSEUDO_DUID,
          values: [...COMPLETED_STATUS_KINDS],
        },
        {
          ...DEFAULT_ASSIGNEES_FILTER_PARTIAL,
          propertyDuid: dataStore.defaultAssigneesProperty.duid,
          values: [CURRENT_USER_PSEUDO_USER_KEY],
          locked: true,
        },
      ];
    },
    defaultFilters(): Filter[] {
      if (!this.currentPage) {
        return [];
      }

      const pageKind = this.currentPage.kind;
      if (pageKind === ViewKind.TRASH) {
        return this.defaultTrashFilters;
      }
      if (pageKind === ViewKind.SEARCH) {
        return this.defaultSearchFilters;
      }
      if (pageKind === ViewKind.MY_TASKS) {
        return this.defaultMyTasksFilters;
      }
      return [];
    },
    filters(): Filter[] {
      return this.layout?.filterGroup.filters ?? [];
    },
    isFilterEqual(): (a: Filter, b: Filter) => boolean {
      return (a, b) => {
        const aCopy = deepCopy(a);
        const bCopy = deepCopy(b);
        aCopy.values.sort(makeStringComparator(String));
        bCopy.values.sort(makeStringComparator(String));
        return equal(aCopy, bCopy, { strict: true });
      };
    },
    areFiltersEqual(): (a: Filter[], b: Filter[]) => boolean {
      return (a, b) => {
        const aCopy = deepCopy(a);
        const bCopy = deepCopy(b);

        /* Sort them so that the equality is order agnostic */
        const sort = (filterArr: Filter[]) => {
          filterArr.forEach((e) => {
            e.values.sort(makeStringComparator(String));
          });
          return filterArr.sort(makeStringComparator((e) => e.propertyDuid));
        };
        sort(aCopy);
        sort(bCopy);

        return equal(aCopy, bCopy, { strict: true });
      };
    },
    usingDefaultFilters(): boolean {
      return this.areFiltersEqual(this.filters, this.defaultFilters);
    },
    usingDefaultFiltersAndSorts(): boolean {
      return this.usingDefaultFilters && this.sorts.length === 0;
    },
    areDefaultSearchFilters(): (filters: Filter[]) => boolean {
      return (filters) => this.areFiltersEqual(filters, this.defaultSearchFilters);
    },
    getNonDefaultSearchFilters(): (filters: Filter[]) => Filter[] {
      return (filters) => {
        const defaultSearchFilterMap = new Map(this.defaultSearchFilters.map((e) => [e.propertyDuid, e]));
        return filters.filter((e) => {
          const defaultFilter = defaultSearchFilterMap.get(e.propertyDuid);
          return !defaultFilter || !this.isFilterEqual(defaultFilter, e);
        });
      };
    },
    getFilter(): (propertyDuid: string) => Filter | undefined {
      return (propertyDuid) => this.filters.find((e) => e.propertyDuid === propertyDuid);
    },
    sorts(): Sort[] {
      return this.layout?.sorts ?? [];
    },
    collapsedGroups(): string[] {
      if (!GROUP_BY_LAYOUT_KINDS.has(this.layoutKind)) {
        return [];
      }

      return (this.layoutConfig as LayoutConfigList | LayoutConfigBoard).collapsedGroups ?? [];
    },
    collapsedSubgroups(): string[] {
      if (this.layoutKind !== LayoutKind.BOARD) {
        return [];
      }

      return (this.layoutConfig as LayoutConfigBoard).collapsedSubgroups ?? [];
    },
    subtaskDisplayMode(): SubtaskDisplayMode {
      return this.layoutConfig.subtaskDisplayMode ?? SubtaskDisplayMode.INDENTED;
    },
    roadmapZoom(): number {
      return (this.layout?.kindConfigMap?.[LayoutKind.ROADMAP] as LayoutConfigRoadmap)?.pxPerDay ?? 10;
    },
    roadmapTaskListWidthPx(): number {
      return (this.layout?.kindConfigMap?.[LayoutKind.ROADMAP] as LayoutConfigRoadmap)?.taskListWidthPx ?? 550;
    },
    calendarDisplayPeriod(): CalendarDisplayPeriod {
      return (this.layout?.kindConfigMap?.[LayoutKind.CALENDAR] as LayoutConfigCalendar)?.displayPeriod ?? "month";
    },
    calendarDisplayPeriodCount(): number {
      return (this.layout?.kindConfigMap?.[LayoutKind.CALENDAR] as LayoutConfigCalendar)?.displayPeriodCount ?? 1;
    },
    showFutureRecurringTasks(): boolean {
      return (
        (this.layout?.kindConfigMap?.[LayoutKind.CALENDAR] as LayoutConfigCalendar)?.showFutureRecurringTasks ?? true
      );
    },
    summaryStatisticKind(): SummaryStatisticKind {
      return this.layout?.summaryStatisticKind ?? SummaryStatisticKind.NONE;
    },
    isLoadingTasksProperty(): (tasks: Task[], propertyDuid: string) => boolean {
      return (tasks, propertyDuid) =>
        tasks.some(({ duid }) => this.loadingTaskProperties.has(makeLoadingAiTaskPropertyKey(duid, propertyDuid)));
    },
    commandsList(): CommandSection[] {
      return getCommandsList(this.$useDataStore());
    },
    // modals
    formInInternalModal(): Form | null {
      if (!this.formInInternalModalDuid) {
        return null;
      }

      return this.$useDataStore().getFormByDuid(this.formInInternalModalDuid) ?? null;
    },
    pageInPermissionsModal(): PageWithPermissions | null {
      const dataStore = this.$useDataStore();

      if (!this.permissionsModalConfig || this.permissionsModalConfig.kind === null) {
        return null;
      }

      switch (this.permissionsModalConfig.kind) {
        case PageKind.DASHBOARD: {
          return dataStore.getDashboardByDuid(this.permissionsModalConfig.duid) ?? null;
        }
        case PageKind.SPACE: {
          return dataStore.getSpaceByDuid(this.permissionsModalConfig.duid) ?? null;
        }
        case PageKind.VIEW: {
          return dataStore.getViewByDuid(this.permissionsModalConfig.duid) ?? null;
        }
        default: {
          throw new Error(`Unknown page with permissions kind: ${this.permissionsModalConfig.kind}`);
        }
      }
    },
    chatInPermissionsModal(): Chat | null {
      if (!this.permissionsModalConfig || this.permissionsModalConfig.kind !== null) {
        return null;
      }

      return this.$useDataStore().getChatByDuidIfLoaded(this.permissionsModalConfig.duid) ?? null;
    },
    linkOpenInModal(): TaskLinkInModal | null {
      if (!this.linkOpenInModalConfig) {
        return null;
      }

      const task = this.$useDataStore().getTaskByDuidIfLoaded(this.linkOpenInModalConfig.taskDuid);
      if (!task) {
        return null;
      }

      const link = task.links.find((e) => e.duid === this.linkOpenInModalConfig?.linkDuid);
      if (!link && this.linkOpenInModalConfig?.linkDuid) {
        return null;
      }

      return { taskDuid: task.duid, link };
    },
    taskOpenInDetail(): Task | null {
      if (!this.taskDetailOpen || this.selectedTaskDuids.size !== 1) {
        return null;
      }

      return this.$useDataStore().getTaskByDuidIfLoaded([...this.selectedTaskDuids][0]) ?? null;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    activeFilterArea(): any {
      if (this.searchFilterArea) {
        return this.searchFilterArea;
      }
      return this.filterArea;
    },
    docOpenInFullscreen(): Doc | null {
      if (!this.docOpenInFullscreenDuid) {
        return null;
      }

      return this.$useDataStore().getDocByDuidIfLoaded(this.docOpenInFullscreenDuid) ?? null;
    },
    trialStartedModalOpen(): boolean {
      const tenantStore = this.$useTenantStore();
      const trialType = tenantStore.getEntitlementValue(Entitlement.TRIAL_TYPE) as Subscription;
      return (
        !tenantStore.isSubscriptionGte(trialType, true) &&
        tenantStore.isTrialActive &&
        (this.$useUserStore().getTutorialStatus(TutorialName.TRIAL_STARTED) ?? 2) < 2 &&
        !this.onboardingModalOpen &&
        !this.onboardingVideoModalOpen &&
        !this.trialStartedSuppressed
      );
    },
    onboardingChecklistCompleted(): boolean {
      return (this.$useUserStore().getTutorialStatus(TutorialName.CHECKLIST_COMPLETED) ?? 1) > 1;
    },
    onboardingVideoModalShouldOpen(): boolean {
      const status = this.$useUserStore().getTutorialStatus(TutorialName.WATCH_THE_VIDEO) ?? 1;
      return status < 2 && !this.onboardingModalOpen && !this.trialStartedModalOpen;
    },
    isModalOtherThanTaskOrCommandCenterOpen(): boolean {
      return [
        this.appDownloadModalOpen,
        this.chatInPermissionsModal,
        this.deleteModalConfig,
        this.feedbackModalOpen,
        this.formInInternalModal,
        this.startBuildModeOverlayOpen,
        this.linkOpenInModal,
        this.mediaOpenInFullscreen,
        this.mobileLeftbarOpen,
        this.movePageModalConfig,
        this.onboardingModalOpen,
        this.onboardingVideoModalOpen,
        this.pageInPermissionsModal,
        this.remapModal,
        this.searchModalOpen,
        this.settingsModalOpen,
        this.shortcutsModalOpen,
        this.switchToDesktopModalOpen,
        this.upgradeModalConfig,
      ].some(Boolean);
    },
  },
  actions: {
    validateAndFixLayoutConfig() {
      if (!this.layout) {
        return;
      }

      const { layoutConfig, layoutKind } = this;
      let needsUpdate = false;

      if (!this.layout.kindConfigMap[layoutKind]) {
        this.layout.kindConfigMap[layoutKind] = layoutConfig;
        needsUpdate = true;
      }

      if (GROUP_BY_LAYOUT_KINDS.has(layoutKind)) {
        const config = layoutConfig as LayoutConfigList | LayoutConfigBoard;
        const validGroupBy = this.groupBy;
        if (config.groupBy !== validGroupBy) {
          config.groupBy = validGroupBy;
          needsUpdate = true;
        }
      }

      if (COLOR_BY_LAYOUT_KINDS.has(layoutKind)) {
        const config = layoutConfig as LayoutConfigRoadmap | LayoutConfigCalendar;
        const validColorBy = this.colorBy;
        if (config.colorBy !== validColorBy) {
          config.colorBy = validColorBy;
          needsUpdate = true;
        }
      }

      if (layoutKind === LayoutKind.BOARD) {
        const config = layoutConfig as LayoutConfigBoard;
        const storedSubgroupBy = config.subgroupBy ?? UNSET_PSEUDO_SUBGROUP_BY;
        if (
          storedSubgroupBy !== UNSET_PSEUDO_SUBGROUP_BY &&
          (storedSubgroupBy === config.groupBy ||
            !this.groupByDefinitionList.find((e) => e.property.duid === storedSubgroupBy))
        ) {
          config.subgroupBy = UNSET_PSEUDO_SUBGROUP_BY;
          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
      }
    },
    init() {
      const dataStore = this.$useDataStore();
      const pageStore = this.$usePageStore();
      const tenantStore = this.$useTenantStore();
      const userStore = this.$useUserStore();

      // update network tracking
      const network = useNetwork();
      watch(
        () => network.isOnline.value,
        (isOnline) => {
          pageStore.isOnline = isOnline;
        }
      );

      // update theme
      const dark = useDark();
      watch(dark, () => nextTick(() => pageStore.updateTheme()));

      watch(
        () => this.groupByDefinitionList,
        () => nextTick(() => this.validateAndFixLayoutConfig()),
        { deep: true }
      );

      // close the RB and fullscreen if not exactly one task is selected
      watch(
        () => this.selectedTaskDuids.size,
        (newSize) => {
          if (newSize === 1) {
            return;
          }
          if (this.taskDetailOpen) {
            this.taskDetailOpen = false;
            this.taskDetailMode = null;
          }
        }
      );

      // manage the focused task in the URL and update recent task stack
      watch(
        () => this.taskOpenInDetail,
        (newTask) => {
          const route = this.$router.currentRoute.value;
          const params = { ...route.params };

          if (newTask && this.currentPageConfig?.kind !== PageKind.INBOX) {
            userStore.addRecentTask(newTask, false);
            if (route.name === "build_chat") {
              if (route.params.taskDuid !== newTask.duid) {
                this.$router.push({ name: "build_chat", params: { taskDuid: newTask.duid }, query: route.query });
              }
              return;
            }
            params.taskDuidAndSlug = getTaskLink(newTask).params.taskDuidAndSlug;
            const newRoute = { ...route, params };
            this.$router.push(newRoute);
            return;
          }

          delete params.taskDuidAndSlug;
          const page = this.currentPage;
          if (page) {
            const newLink = getPageLink(page, dataStore.getSpaceByDuid);
            if ("params" in newLink) {
              Object.assign(params, newLink.params);
            }
          }
          const newRoute = { ...route, params };
          this.$router.push(newRoute);
        }
      );

      // remove reminder indicator when a task is opened
      watch(
        () => this.taskOpenInDetail,
        (newTask) => {
          if (!newTask || !newTask.remindAt || moment(newTask.remindAt).isAfter(moment())) {
            return;
          }
          dataStore.updateTasks([{ duid: newTask.duid, remindAt: null }], { noUndo: true });
        }
      );

      watch(
        () => tenantStore.name,
        (newTenantName) => {
          const fullTitle = [getPageDisplayName(this.currentPage, dataStore.getSpaceByDuid), newTenantName]
            .filter(Boolean)
            .join(" | ");
          pageStore.pageTitle = fullTitle;
          document.title = fullTitle;
        }
      );

      // update the URL when the title of the current page changes
      watch(
        () => getPageDisplayName(this.currentPage, dataStore.getSpaceByDuid),
        (newTitle) => {
          if (!this.currentPage) {
            return;
          }

          const router = this.$router;

          const page = this.currentPage;
          const query = { ...router.currentRoute.value.query };

          const fullTitle = [newTitle, tenantStore.name].filter(Boolean).join(" | ");
          pageStore.pageTitle = fullTitle;
          document.title = fullTitle;

          switch (page.pageKind) {
            case PageKind.DARTBOARD: {
              if (!router.currentRoute.value.params.taskDuidAndSlug) {
                router.replace({
                  params: { slug: getDartboardLink(page, dataStore.getSpaceByDuid).params.slug },
                  query,
                });
              }
              break;
            }
            case PageKind.DASHBOARD: {
              if (!router.currentRoute.value.params.taskDuidAndSlug) {
                router.replace({ params: { slug: getDashboardLink(page).params?.slug }, query });
              }
              break;
            }
            case PageKind.VIEW: {
              if (!router.currentRoute.value.params.taskDuidAndSlug) {
                router.replace({ params: { slug: getViewLink(page).params?.slug }, query });
              }
              break;
            }
            case PageKind.FOLDER: {
              if (!router.currentRoute.value.params.docDuid) {
                router.replace({ params: { slug: getFolderLink(page).params.slug }, query });
              }
              break;
            }
            case PageKind.FORM: {
              router.replace({ params: { slug: getPublicFormLink(page).params.slug }, query });
              break;
            }
            case PageKind.AGENTS:
            case PageKind.DASHBOARDS_ROOT:
            case PageKind.HOME:
            case PageKind.INBOX:
            case PageKind.NOT_FOUND:
            case PageKind.VIEWS_ROOT: {
              break;
            }
            default: {
              throw new Error(`Unknown page kind: ${page.pageKind}`);
            }
          }
        }
      );

      // update the URL when the title of the doc changes
      watch(
        () => this.docOpenInFullscreen?.title,
        () => {
          const doc = this.docOpenInFullscreen;
          if (!doc) {
            return;
          }
          userStore.addRecentDoc(doc, false);

          this.$router.replace({ params: { slug: getDocLink(doc).params.slug } });
        }
      );

      // update the URL when the title of the task changes
      watch(
        () => this.taskOpenInDetail?.title,
        () => {
          const task = this.taskOpenInDetail;
          if (!task || this.$router.currentRoute.value.name === "build_chat") {
            return;
          }

          this.$router.replace({
            params: { slugSep: "", slug: "", taskDuidAndSlug: getTaskLink(task).params.taskDuidAndSlug },
            query: { ...this.$router.currentRoute.value.query },
          });
        }
      );
    },
    // global app data
    getWorking(): boolean {
      // TODO transition back to computed--it wasn't recomputing properly
      const transactionManagerWorking = this.$requestManager.working.value;
      const someThrottleManagerWorking = allThrottleManagers.value.some((e) => e.working);
      const hasActiveRecording = this.hasActiveSpeechRecording;
      const { hasInvalidAgentState } = this;
      return transactionManagerWorking || someThrottleManagerWorking || hasActiveRecording || hasInvalidAgentState;
    },
    accelerateSave() {
      allThrottleManagers.value.forEach((e) => e.finish());
    },
    setIsRecording(duid: string, isRecording: boolean) {
      if (isRecording) {
        this.speechRecordingDuids.add(duid);
      } else {
        this.speechRecordingDuids.delete(duid);
      }
    },
    setEventKinds(eventKinds: EventKindFull[]) {
      this.eventKinds = eventKinds;
    },
    setPageIconOptions(pageIconOptions: PageIconOption[]) {
      this.pageIconOptions = pageIconOptions;
    },
    setWebhookEventKinds(webhookEventKinds: WebhookEventKind[]) {
      this.webhookEventKinds = webhookEventKinds;
    },
    // components and managers
    connectWs(websocketManager: WebsocketManager) {
      const pageStore = this.$usePageStore();
      if (!pageStore.isLoggedIn || pageStore.isPublicView) {
        return;
      }
      this.websocketManager = websocketManager;
      this.websocketManager.connect();
    },
    disconnectWs() {
      if (!this.websocketManager) {
        return;
      }
      this.websocketManager.disconnect();
      this.websocketManager = undefined;
    },
    // visualization
    getActiveVisualization(): Visualization {
      if (this.tcmSubtasksList) {
        return getListVisualization(
          () => null,
          () => this.tcmSubtasksList?.api ?? null
        );
      }

      if (this.taskDetailSubtasksList) {
        const subtasksVisualization = getListVisualization(
          () => null,
          () => this.taskDetailSubtasksList?.api ?? null
        );
        if (subtasksVisualization.getSelectedRows().length > 0) {
          return subtasksVisualization;
        }
      }

      return this.getBaseVisualization();
    },
    getBaseVisualization(): Visualization {
      if (this.list) {
        return getListVisualization(
          () => this.list,
          () => this.list?.api ?? null
        );
      }
      if (this.board || this.calendar || this.workflow) {
        return getBoardCalendarOrWorkflowVisualization(() => this.board ?? this.calendar ?? this.workflow, this);
      }
      if (this.roadmap) {
        return getListVisualization(
          () => this.roadmap,
          () => this.roadmap?.api ?? null
        );
      }
      if (this.inbox) {
        return getInboxVisualization(() => this.inbox, this);
      }
      return getDefaultVisualization(() => null, this);
    },
    getSubtasksListVisualization(): Visualization | undefined {
      if (this.tcmSubtasksList) {
        return getListVisualization(
          () => null,
          () => this.tcmSubtasksList?.api ?? null
        );
      }

      if (this.taskDetailSubtasksList) {
        return getListVisualization(
          () => null,
          () => this.taskDetailSubtasksList?.api ?? null
        );
      }

      return undefined;
    },
    // instance-specific
    _updateLayoutField(
      field: keyof Layout,
      value: string | object | object[],
      options?: { awaitBackend?: boolean }
    ): Promise<Layout | undefined> {
      if (!this.layout) {
        return Promise.resolve(undefined);
      }

      return this.$useDataStore().updateLayout({ duid: this.layout.duid, [field]: value }, options);
    },
    async setCurrentPage(page: Page) {
      const dataStore = this.$useDataStore();
      const userStore = this.$useUserStore();

      const newPageKind = page.pageKind;
      const newPageDuid = page.duid;
      if (
        this.currentPageConfig &&
        this.currentPageConfig.kind === newPageKind &&
        this.currentPageConfig.duid === newPageDuid
      ) {
        return;
      }

      if (!this.$usePageStore().isPublic) {
        const lastUrlPath = this.$router.currentRoute.value.path;
        if (lastUrlPath !== userStore.lastUrlPath) {
          userStore.lastUrlPath = lastUrlPath;
          this.$backendOld.profile.edit("lastUrlPath", lastUrlPath);
        }
      }

      this.currentPageConfig = { kind: newPageKind, duid: newPageDuid };

      this.hoverRow = null;
      this.selectedTaskDuids = new Set();

      // clear the search when navigating away from a search view
      if (page.kind !== ViewKind.SEARCH) {
        this.setSearch(null);
      }

      try {
        this.getActiveVisualization().deselectAll();
      } catch {
        // ignore
      }
      dataStore.clearHistoryStack();

      const existingLayout =
        newPageKind === PageKind.VIEW || newPageKind === PageKind.DASHBOARD
          ? dataStore.getLayoutByDuid(page.layoutDuid)
          : dataStore.getLayoutByDartboardDuid(newPageDuid);

      if (existingLayout) {
        this.layoutDuid = existingLayout.duid;
        return;
      }

      const newLayout = await dataStore.createLayoutForDartboard(newPageDuid, this.defaultFilters);

      this.layoutDuid = newLayout?.duid;
    },
    setLayoutKind(kind: LayoutKind) {
      if (!this.layout) {
        return;
      }

      this.layout.kind = kind;
      this._updateLayoutField("kind", this.layout.kind);

      this.selectedTaskDuids = new Set();
      this.list?.api?.deselectAll();

      nextTick(() => this.validateAndFixLayoutConfig());
    },
    setPropertyDuidVisibility(propertyDuid: string, shown: boolean, propertyOrderDuids?: string[]) {
      if (
        !this.currentPage ||
        !(this.currentPage.pageKind === PageKind.DARTBOARD || this.currentPage.pageKind === PageKind.VIEW)
      ) {
        return;
      }

      const dataStore = this.$useDataStore();
      const update: DartboardUpdate | ViewUpdate = { duid: this.currentPage.duid };

      if (shown) {
        if (propertyOrderDuids) {
          update.propertyOrderDuids = propertyOrderDuids;
        }
        if (!this.alwaysShownPropertyDuids.includes(propertyDuid)) {
          update.alwaysShownPropertyDuids = [...this.currentPage.alwaysShownPropertyDuids, propertyDuid];
        }
        if (this.currentPage.alwaysHiddenPropertyDuids.includes(propertyDuid)) {
          update.alwaysHiddenPropertyDuids = this.currentPage.alwaysHiddenPropertyDuids.filter(
            (e) => e !== propertyDuid
          );
        }
      } else {
        if (!this.currentPage.alwaysHiddenPropertyDuids.includes(propertyDuid)) {
          update.alwaysHiddenPropertyDuids = [...this.currentPage.alwaysHiddenPropertyDuids, propertyDuid];
        }
        if (this.currentPage.alwaysShownPropertyDuids.includes(propertyDuid)) {
          update.alwaysShownPropertyDuids = this.currentPage.alwaysShownPropertyDuids.filter((e) => e !== propertyDuid);
        }
      }

      if (Object.keys(update).length === 1) {
        return;
      }

      dataStore.updatePage(update, this.currentPage.pageKind);
    },
    setPropertyOrderDuids(orderDuids: string[]) {
      if (
        !this.currentPage ||
        !(this.currentPage.pageKind === PageKind.DARTBOARD || this.currentPage.pageKind === PageKind.VIEW)
      ) {
        return;
      }

      const dataStore = this.$useDataStore();
      const update: DartboardUpdate | ViewUpdate = { duid: this.currentPage.duid, propertyOrderDuids: orderDuids };

      dataStore.updatePage(update, this.currentPage.pageKind);
    },
    setShowAbsentees(showAbsentees: boolean) {
      if (!this.layout) {
        return;
      }

      this.layoutConfig.showAbsentees = showAbsentees;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setGroupBy(groupBy: string) {
      if (!this.layout || !GROUP_BY_LAYOUT_KINDS.has(this.layoutKind)) {
        return;
      }

      const layoutConfig = this.layoutConfig as LayoutConfigList | LayoutConfigBoard;
      layoutConfig.groupBy = groupBy;
      layoutConfig.collapsedGroups = [];

      if (this.layoutKind === LayoutKind.BOARD) {
        const boardConfig = layoutConfig as LayoutConfigBoard;
        const subgroupByDefinition = this.groupByDefinitionList.find((e) => e.property.duid === boardConfig.subgroupBy);
        if (!subgroupByDefinition || boardConfig.subgroupBy === groupBy) {
          boardConfig.subgroupBy = UNSET_PSEUDO_SUBGROUP_BY;
        }
      }

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setHoverGroupId(groupId: string | null) {
      this.hoverGroupId = groupId;
    },
    setHideEmptyGroups(hideEmptyGroups: boolean) {
      if (!this.layout || !GROUP_BY_LAYOUT_KINDS.has(this.layoutKind)) {
        return;
      }

      const layoutConfig = this.layoutConfig as LayoutConfigList | LayoutConfigBoard;
      layoutConfig.hideEmptyGroups = hideEmptyGroups;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setColorBy(colorBy: string) {
      if (!this.layout || !COLOR_BY_LAYOUT_KINDS.has(this.layoutKind)) {
        return;
      }

      const layoutConfig = this.layoutConfig as LayoutConfigRoadmap | LayoutConfigCalendar;
      layoutConfig.colorBy = colorBy;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setSubgroupBy(subgroupBy: string) {
      if (!this.layout || this.layoutKind !== LayoutKind.BOARD) {
        return;
      }

      const layoutConfig = this.layoutConfig as LayoutConfigBoard;
      layoutConfig.subgroupBy = subgroupBy;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setDisplayCoverImages(displayCoverImages: boolean) {
      if (!this.layout || this.layoutKind !== LayoutKind.BOARD) {
        return;
      }

      const layoutConfig = this.layoutConfig as LayoutConfigBoard;
      layoutConfig.displayCoverImages = displayCoverImages;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    resetFilters() {
      if (!this.layout) {
        return;
      }

      this.layout.filterGroup.filters = deepCopy(this.defaultFilters);
      this._updateLayoutField("filterGroup", this.layout.filterGroup);
    },
    setFilter(filter: Filter, options?: { awaitBackend?: boolean }): Promise<Layout | undefined> {
      if (!this.layout) {
        return Promise.resolve(undefined);
      }

      if (filter.propertyDuid !== AI_PROPERTY_PSEUDO_DUID) {
        this.removeFilter(AI_PROPERTY_PSEUDO_DUID);
      }

      const index = this.layout.filterGroup.filters.findIndex((e) => e.propertyDuid === filter.propertyDuid);
      this.layout.filterGroup.filters[index === -1 ? this.layout.filterGroup.filters.length : index] = filter;
      return this._updateLayoutField("filterGroup", this.layout.filterGroup, options);
    },
    setAllFilters(filters: Filter[]) {
      if (!this.layout) {
        return;
      }

      this.layout.filterGroup.filters = filters;
      this._updateLayoutField("filterGroup", this.layout.filterGroup);
    },
    removeFilter(propertyDuid: string, options?: { awaitBackend?: boolean }): Promise<Layout | undefined> {
      if (!this.layout) {
        return Promise.resolve(undefined);
      }

      const index = this.layout.filterGroup.filters.findIndex((e) => e.propertyDuid === propertyDuid);
      if (index === -1) {
        return Promise.resolve(undefined);
      }
      this.layout.filterGroup.filters.splice(index, 1);
      return this._updateLayoutField("filterGroup", this.layout.filterGroup, options);
    },
    setAllSorts(sorts: Sort[]) {
      if (!this.layout || equal(sorts, this.layout.sorts, { strict: true })) {
        return;
      }

      this.layout.sorts = sorts;
      this._updateLayoutField("sorts", this.layout.sorts);
    },
    resetSorts() {
      this.setAllSorts([]);
    },
    setGroupCollapsed(column: string, collapsed: boolean) {
      if (!this.layout || !GROUP_BY_LAYOUT_KINDS.has(this.layoutKind)) {
        return;
      }

      const layoutConfig = this.layoutConfig as LayoutConfigList | LayoutConfigBoard;
      let collapsedGroups = deepCopy(layoutConfig.collapsedGroups);
      if (collapsed) {
        collapsedGroups.push(column);
      } else {
        collapsedGroups = collapsedGroups.filter((e) => e !== column);
      }
      layoutConfig.collapsedGroups = collapsedGroups;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setSubgroupCollapsed(subgroupId: string, collapsed: boolean) {
      if (!this.layout || this.layoutKind !== LayoutKind.BOARD) {
        return;
      }

      const layoutConfig = this.layoutConfig as LayoutConfigBoard;
      let collapsedSubgroups = deepCopy(layoutConfig.collapsedSubgroups ?? []);
      if (collapsed) {
        if (!collapsedSubgroups.includes(subgroupId)) {
          collapsedSubgroups.push(subgroupId);
        }
      } else {
        collapsedSubgroups = collapsedSubgroups.filter((e) => e !== subgroupId);
      }
      layoutConfig.collapsedSubgroups = collapsedSubgroups;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    getColumnWidthParams(propertyDuid: string): {
      width?: number;
      flex?: number;
      minWidth?: number;
      maxWidth?: number;
    } {
      const dataStore = this.$useDataStore();
      const pageStore = this.$usePageStore();

      if (
        !this.currentPage ||
        !(this.currentPage.pageKind === PageKind.DARTBOARD || this.currentPage.pageKind === PageKind.VIEW)
      ) {
        return {};
      }

      const propertyKind = dataStore.getPropertyByDuid(propertyDuid)?.kind ?? propertyDuid;
      if (pageStore.isMobile) {
        if (propertyKind === PropertyKind.DEFAULT_TITLE) {
          return { flex: 1 };
        }
        if (propertyKind === PropertyKind.DEFAULT_STATUS) {
          return { width: STATUS_COLUMN_MOBILE_WIDTH };
        }
        if (propertyKind === PropertyKind.DEFAULT_ASSIGNEES) {
          return { width: ASSIGNEES_COLUMN_MOBILE_WIDTH };
        }
        return {};
      }

      if (propertyDuid === CONFIG_PROPERTY_PSEUDO_DUID) {
        return { flex: 1, minWidth: 28 };
      }

      const width =
        this.currentPage.propertyWidthMap[propertyDuid] ?? PROPERTY_KIND_TO_DEFAULT_WIDTH_MAP.get(propertyKind);

      if (propertyKind === PropertyKind.DEFAULT_TITLE) {
        const containerWidth =
          this.layoutKind === LayoutKind.ROADMAP ? this.roadmapTaskListWidthPx : (this.list?.width ?? 1000);
        return {
          width,
          minWidth: TITLE_COLUMN_MIN_WIDTH,
          maxWidth: Math.max(containerWidth * 0.66, TITLE_COLUMN_MAX_MIN_WIDTH),
        };
      }

      return { width, ...DEFAULT_COLUMN_WIDTH_LIMITS };
    },
    setSubtaskDisplayMode(subtaskDisplayMode: SubtaskDisplayMode) {
      if (!this.layout) {
        return;
      }

      this.layoutConfig.subtaskDisplayMode = subtaskDisplayMode;
      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setRoadmapZoom(pxPerDay: number) {
      if (!this.layout || this.layout.kind !== LayoutKind.ROADMAP) {
        return;
      }

      (this.layoutConfig as LayoutConfigRoadmap).pxPerDay = pxPerDay;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setRoadmapTaskListWidthPx(taskListWidthPx: number) {
      if (!this.layout || this.layout.kind !== LayoutKind.ROADMAP) {
        return;
      }

      (this.layoutConfig as LayoutConfigRoadmap).taskListWidthPx = taskListWidthPx;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setCalendarDisplayPeriod(type: CalendarDisplayPeriod) {
      if (!this.layout || this.layout.kind !== LayoutKind.CALENDAR) {
        return;
      }

      (this.layoutConfig as LayoutConfigCalendar).displayPeriod = type;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setCalendarDisplayPeriodCount(count: number) {
      if (!this.layout || this.layout.kind !== LayoutKind.CALENDAR) {
        return;
      }

      (this.layoutConfig as LayoutConfigCalendar).displayPeriodCount = count;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setShowFutureRecurringTasks(show: boolean) {
      if (!this.layout || this.layout.kind !== LayoutKind.CALENDAR) {
        return;
      }

      (this.layoutConfig as LayoutConfigCalendar).showFutureRecurringTasks = show;

      this._updateLayoutField("kindConfigMap", this.layout.kindConfigMap);
    },
    setSummaryStatisticKind(summaryStatisticKind: SummaryStatisticKind) {
      if (!this.layout) {
        return;
      }

      this.layout.summaryStatisticKind = summaryStatisticKind;
      this._updateLayoutField("summaryStatisticKind", this.layout.summaryStatisticKind);
    },
    startLoadingAiTaskProperties(taskDuids: string[], propertyDuid: string) {
      taskDuids.forEach((e) => this.loadingTaskProperties.add(makeLoadingAiTaskPropertyKey(e, propertyDuid)));
    },
    finishLoadingAiTaskProperties(taskDuids: string[], propertyDuid: string) {
      taskDuids.forEach((e) => this.loadingTaskProperties.delete(makeLoadingAiTaskPropertyKey(e, propertyDuid)));
    },
    setSearch(search: string | null) {
      this.search = search;
    },
    // modals
    _ensureAllModalsClosedExcept(modal?: ModalKind) {
      if (modal !== ModalKind.AGENT_CREATION) {
        this.setAgentCreationModal(null);
      }
      if (modal !== ModalKind.APP_DOWNLOAD) {
        this.setAppDownloadModalOpen(false);
      }
      if (modal !== ModalKind.COMMAND_CENTER) {
        this.setCommandCenterOpen(false);
      }
      if (modal !== ModalKind.CREATE_TASK && modal !== ModalKind.LINK && modal !== ModalKind.REMAP_MODAL) {
        this.setTcmOpen(false);
      }
      if (modal !== ModalKind.FEEDBACK) {
        this.setFeedbackModalOpen(false);
      }
      if (modal !== ModalKind.INTERNAL_FORM) {
        this.setFormOpenInInternalModal(null);
      }
      if (modal !== ModalKind.START_BUILD_MODE) {
        this.setStartBuildModeOverlayOpen(false);
      }
      if (modal !== ModalKind.FULLSCREEN_MEDIA) {
        this.setFullscreenMediaModalOpen(null);
      }
      if (modal !== ModalKind.LINK) {
        this.setLinkOpenInModal(null);
      }
      if (modal !== ModalKind.MOBILE_LEFTBAR) {
        this.setMobileLeftbarOpen(false);
      }
      if (modal !== ModalKind.ONBOARDING_VIDEO) {
        this.setOnboardingVideoModalOpen(false);
      }
      if (modal !== ModalKind.PERMISSIONS) {
        this.setPageInPermissionsModal(null);
      }
      if (modal !== ModalKind.REMAP_MODAL) {
        this.setRemapModal(null);
      }
      if (modal !== ModalKind.SEARCH) {
        this.setSearchModalOpen(false);
      }
      if (modal !== ModalKind.SETTINGS) {
        this.setSettingsModalOpen(false);
      }
      if (modal !== ModalKind.SHORTCUTS) {
        this.setShortcutsModalOpen(false);
      }
      if (modal !== ModalKind.SWITCH_TO_DESKTOP) {
        this.setSwitchToDesktopModalOpen(false);
      }
      if (this.trialStartedModalOpen) {
        this.$useUserStore().updateTutorialStatuses([{ name: TutorialName.TRIAL_STARTED, status: 2 }]);
      }
    },
    setCommandCenterOpen(open: boolean, commandId?: CommandId, hideSubmenuTitle = false) {
      if (this.cmdCtr) {
        this.cmdCtr.open(open, commandId, hideSubmenuTitle);
      }
    },
    openContextMenu(
      event: MouseEvent,
      sections: DropdownMenuSection[],
      options: { widthPixels?: number; maxHeightPixels?: number } = {}
    ) {
      const { widthPixels, maxHeightPixels } = options;

      const target = event?.target as HTMLElement;

      if (event && target) {
        event.preventDefault();
        event.stopPropagation();
        this.contextMenu = {
          node: target,
          sections: markRaw(sections),
          clientX: event.clientX,
          clientY: event.clientY,
          widthPixels,
          maxHeightPixels,
        };
      }
    },
    openTaskPreviewTooltip(target: Element, scrollableParent: Element, task: Task, placement?: Placement) {
      this.taskPreviewTooltip = { node: target, scrollableParent, task, placement };
    },
    showFeedbackTooltip(node: Element | null, recommendationDuids: string[]) {
      if (!node) {
        this.feedbackTooltip = null;
        return;
      }

      this.feedbackTooltip = { node, recommendationDuids };
    },
    setSearchModalOpen(open: boolean) {
      if (open) {
        this._ensureAllModalsClosedExcept(ModalKind.SEARCH);
        this.searchModalVisible = true;
      }
      this.searchModalOpen = open;
    },
    setSearchModalVisible(visible: boolean) {
      this.searchModalVisible = visible;
    },
    setSettingsModalOpen(open: boolean) {
      if (open) {
        this._ensureAllModalsClosedExcept(ModalKind.SETTINGS);
      } else if (this.settingsModalOpen) {
        // oxlint-disable-next-line dart/no-set-timeout
        setTimeout(() => {
          this.$router.push(this.$routerUtils.makeLinkToSettingsRef(undefined).value);
        }, 300);
      }
      this.settingsModalOpen = open;
    },
    setLastSettingsPage(query: Record<string, string>) {
      this.lastSettingsPage = query;
    },
    setTcmOpen(open: boolean) {
      if (open) {
        this._ensureAllModalsClosedExcept(ModalKind.CREATE_TASK);
      }
      this.tcmOpen = open;
    },
    setStartBuildModeOverlayOpen(open: boolean) {
      if (open) {
        this._ensureAllModalsClosedExcept(ModalKind.START_BUILD_MODE);
      }
      this.startBuildModeOverlayOpen = open;
    },
    setFormOpenInInternalModal(form: Form | null) {
      if (form) {
        this._ensureAllModalsClosedExcept(ModalKind.INTERNAL_FORM);
      }
      this.formInInternalModalDuid = form?.duid ?? null;
    },
    setUpgradeConfirmationModalOpen(
      config: {
        featureAction: string;
        subscription: Subscription;
      } | null
    ) {
      this.upgradeModalConfig = config;
    },
    setDeleteConfirmationModalOpen(
      config: {
        title: string;
        description: string;
        confirmText?: string;
        cancelText?: string;
        onDelete: () => void;
      } | null
    ) {
      this.deleteModalConfig = config;
    },
    setMovePageConfirmationModalOpen(config: { page: Dartboard | Folder; newSpaceDuid: string } | null) {
      this.movePageModalConfig = config;
    },
    movePageToSpace(page: Dartboard | Folder, newSpaceDuid: string) {
      const dataStore = this.$useDataStore();
      if (spacePermissionsSame(dataStore.getSpaceByDuid(newSpaceDuid), dataStore.getSpaceByDuid(page.spaceDuid))) {
        dataStore.updatePage({ duid: page.duid, spaceDuid: newSpaceDuid, order: page.order }, page.pageKind);
        return;
      }

      this.setMovePageConfirmationModalOpen({ page, newSpaceDuid });
    },
    setAgentCreationModal(config: { pendingSetupDuid: string | null } | null) {
      if (config) {
        this._ensureAllModalsClosedExcept(ModalKind.AGENT_CREATION);
      }
      this.agentCreationModal = config;
    },
    setAppDownloadModalOpen(open: boolean) {
      if (open) {
        this._ensureAllModalsClosedExcept(ModalKind.APP_DOWNLOAD);
      }
      this.appDownloadModalOpen = open;
    },
    setFeedbackModalOpen(open: boolean) {
      if (open) {
        this._ensureAllModalsClosedExcept(ModalKind.FEEDBACK);
      }
      this.feedbackModalOpen = open;
    },
    setFullscreenMediaModalOpen(
      mediaOpenInFullscreen: {
        attachmentFileUrl: string;
        attachmentName: string;
        kind: string;
        entityName: string;
      } | null
    ) {
      if (mediaOpenInFullscreen) {
        this._ensureAllModalsClosedExcept(ModalKind.FULLSCREEN_MEDIA);
        this.mediaOpenInFullscreen = this.mediaOpenInFullscreen
          ? Object.assign(this.mediaOpenInFullscreen, mediaOpenInFullscreen)
          : mediaOpenInFullscreen;
      } else {
        this.mediaOpenInFullscreen = null;
      }
    },
    setShortcutsModalOpen(open: boolean) {
      if (open) {
        this._ensureAllModalsClosedExcept(ModalKind.SHORTCUTS);
      }
      this.shortcutsModalOpen = open;
    },
    setSwitchToDesktopModalOpen(open: boolean) {
      if (open) {
        this._ensureAllModalsClosedExcept(ModalKind.SWITCH_TO_DESKTOP);
      }
      this.switchToDesktopModalOpen = open;
    },
    setPageInPermissionsModal(page: PageWithPermissions | null) {
      if (page) {
        this._ensureAllModalsClosedExcept(ModalKind.PERMISSIONS);
      }
      this.permissionsModalConfig = page ? { kind: page.pageKind, duid: page.duid } : null;
    },
    setChatInPermissionsModal(chat: Chat | null) {
      if (chat) {
        this._ensureAllModalsClosedExcept(ModalKind.PERMISSIONS);
      }
      this.permissionsModalConfig = chat ? { kind: null, duid: chat.duid } : null;
    },
    setLinkOpenInModal(link: TaskLinkInModal | null) {
      if (link) {
        this._ensureAllModalsClosedExcept(ModalKind.LINK);
      }

      this.linkOpenInModalConfig = link ? { taskDuid: link.taskDuid, linkDuid: link.link?.duid } : null;
    },
    setOnboardingModalOpen(open: boolean) {
      if (open) {
        this._ensureAllModalsClosedExcept(ModalKind.ONBOARDING);
      } else if (this.onboardingModalOpen) {
        this.setTrialStartedSuppressed(true);
      }
      this.onboardingModalOpen = open;
    },
    setOnboardingVideoModalOpen(open: boolean) {
      if (open) {
        this._ensureAllModalsClosedExcept(ModalKind.ONBOARDING_VIDEO);
      }
      this.onboardingVideoModalOpen = open;
    },
    openTaskDetailForTask(taskDuid: string, mode?: TaskDetailMode, noLog?: boolean) {
      this.selectedTaskDuids = new Set([taskDuid]);
      this.setTaskDetailOpen(true, mode, noLog);
    },
    setTaskDetailOpen(open: boolean, mode?: TaskDetailMode, noLog?: boolean) {
      const pageStore = this.$usePageStore();
      // Always open the rightbar on mobile or public views
      const modeNorm =
        pageStore.isMobile || pageStore.isPublicView
          ? TaskDetailMode.RIGHTBAR
          : (mode ?? this.taskDetailMode ?? this.$useUserStore().taskDetailMode);

      if (!open) {
        this.taskDetailPaneWidthPx = null;
      } else if (modeNorm === TaskDetailMode.RIGHTBAR && this.taskDetailPaneWidthPx === null) {
        this.taskDetailPaneWidthPx = this.$useUserStore().rightbarWidthPx;
      }

      if (
        (!open && !this.taskDetailOpen) ||
        (open && this.taskDetailOpen && (mode === undefined || modeNorm === this.taskDetailMode))
      ) {
        return;
      }
      const eventKind = TASK_DETAIL_MODE_TO_EVENT_KIND_MAP[modeNorm];

      // If it's open and staying open but just switch modes, close and then open to give components (particularly descriptions) a chance to reset
      const wasOpen = this.taskDetailOpen;
      if (!open || wasOpen) {
        this.taskDetailOpen = false;
        this.taskDetailMode = null;
      }
      if (open) {
        const setOpen = () => {
          this.taskDetailOpen = true;
          this.taskDetailMode = modeNorm;
        };
        if (!wasOpen) {
          setOpen();
        } else {
          nextTick(setOpen);
        }
      }

      if (!pageStore.isPublicView && !noLog) {
        const taskDuid = this.taskOpenInDetail?.duid;
        this.$backend.event.create(eventKind, taskDuid ? { open: true, taskDuid } : { open: false });
      }
    },
    setTaskDetailLoading(loading: boolean) {
      this.taskDetailLoading = loading;
    },
    setTaskDetailPaneWidthPx(width: number | null) {
      this.taskDetailPaneWidthPx = width;
    },
    setMobileLeftbarOpen(open: boolean) {
      if (open) {
        this._ensureAllModalsClosedExcept(ModalKind.MOBILE_LEFTBAR);
      }
      this.mobileLeftbarOpen = open;
    },
    startLeftbarHover() {
      if (this.leftbarHoverCloseTimeout) {
        clearTimeout(this.leftbarHoverCloseTimeout);
        this.leftbarHoverCloseTimeout = null;
      }
      this.leftbarHoverOpen = true;
    },
    endLeftbarHover(immediate = false) {
      const userStore = this.$useUserStore();
      const isDesktopLeftbarCollapsed = this.leftbarInCollapsibleMode || !userStore.getLeftbarExpanded();

      if (this.leftbarHoverCloseTimeout) {
        clearTimeout(this.leftbarHoverCloseTimeout);
        this.leftbarHoverCloseTimeout = null;
      }

      if (this.leftbarLocked) {
        return;
      }

      if (immediate || !isDesktopLeftbarCollapsed) {
        this.leftbarHoverOpen = false;
        return;
      }

      // oxlint-disable-next-line dart/no-set-timeout
      this.leftbarHoverCloseTimeout = setTimeout(() => {
        this.leftbarHoverOpen = false;
        this.leftbarHoverCloseTimeout = null;
      }, LEFTBAR_HOVER_CLOSE_TIMEOUT_MS);
    },
    addLeftbarLock(source: string) {
      this.leftbarLockSources.add(source);
      if (this.leftbarHoverCloseTimeout) {
        clearTimeout(this.leftbarHoverCloseTimeout);
        this.leftbarHoverCloseTimeout = null;
      }
    },
    removeLeftbarLock(source: string) {
      if (!this.leftbarLockSources.delete(source)) {
        return;
      }
      if (this.leftbarLockSources.size === 0) {
        this.endLeftbarHover();
      }
    },
    setDocOpenInFullscreen(doc: Doc | null) {
      this.docOpenInFullscreenDuid = doc?.duid ?? null;
    },
    setTrialStartedSuppressed(newTrialStartedSuppressed: boolean) {
      this.trialStartedSuppressed = newTrialStartedSuppressed;
    },
    setRemapModal(remap: RemapModalConfig | null) {
      this.remapModal = remap;
    },
    setChatFullscreen(chatFullscreen: boolean) {
      this.chatFullscreen = chatFullscreen;
    },
  },
});

export default useAppStore;
